import { useEffect } from 'react';
import classNames from 'clsx';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  useConsent,
  type PrivacyTriggerPlacement,
} from '~/core/consent/ConsentProvider';

type PrivacyChoicesTriggerProps = {
  variant: 'sidebar' | 'mobile' | 'editor' | 'footer';
  collapsed?: boolean;
};

export default function PrivacyChoicesTrigger({
  variant,
  collapsed = false,
}: PrivacyChoicesTriggerProps) {
  const { choice, ready, openPreferences, registerPrivacyTrigger } =
    useConsent();
  const placement: PrivacyTriggerPlacement =
    variant === 'sidebar'
      ? 'desktop'
      : variant === 'mobile'
        ? 'compact'
        : 'all';

  useEffect(
    () => registerPrivacyTrigger(placement),
    [placement, registerPrivacyTrigger],
  );

  if (!ready || !choice) return null;

  const iconOnly =
    variant === 'mobile' ||
    variant === 'editor' ||
    (variant === 'sidebar' && collapsed);

  return (
    <button
      type="button"
      aria-label="Privacy choices"
      title="Privacy choices"
      data-cy="privacy-choices-trigger"
      onClick={(event) => openPreferences(event.currentTarget)}
      className={classNames({
        'privacy-choices-footer': variant === 'footer',
        'flex min-h-11 items-center rounded-lg text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2':
          variant !== 'footer',
        'w-full gap-2 px-3 text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-dark-800/40 focus-visible:outline-primary':
          variant === 'sidebar' && !collapsed,
        'w-full justify-center text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-dark-800/40 focus-visible:outline-primary':
          variant === 'sidebar' && collapsed,
        'min-w-11 justify-center text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-dark-800 focus-visible:outline-primary':
          variant === 'mobile',
        'min-w-11 justify-center text-apple-subtext hover:bg-white/10 hover:text-apple-text focus-visible:outline-apple-blue':
          variant === 'editor',
      })}
    >
      {variant !== 'footer' && (
        <ShieldCheckIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      )}
      {!iconOnly && <span>Privacy choices</span>}
    </button>
  );
}
